import React from 'react';
import { Link } from 'react-router-dom';

// Importing the local Logo.png file
import Logo from './Logo.png'; 

// --- Inline SVG Icon Components (Ensuring stability) ---

const IconWrapper = ({ children, size = 20, className = '' }) => (
    <svg 
        xmlns="http://www.w3.org/2000/svg" 
        width={size} 
        height={size} 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke="currentColor" 
        strokeWidth="2" 
        strokeLinecap="round" 
        strokeLinejoin="round" 
        className={className}
    >
        {children}
    </svg>
);

const InstagramIcon = ({ size = 24 }) => (
    <IconWrapper size={size}>
        <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
        <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
        <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
    </IconWrapper>
);

// Custom Chat Icon for WhatsApp link
const WhatsappChatIcon = ({ size = 24 }) => (
    <IconWrapper size={size}>
        {/* Simplified Chat Bubble icon for recognition */}
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
    </IconWrapper>
);

// YouTube Icon
const YoutubeIcon = ({ size = 24 }) => (
    <IconWrapper size={size}>
        <path d="M2.5 17.5V6.5a2 2 0 0 1 2-2h15a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2h-15a2 2 0 0 1-2-2z"></path>
        <polygon points="10 8 16 12 10 16 10 8"></polygon>
    </IconWrapper>
);

const MapPinIcon = ({ size = 20, className = '' }) => (
    <IconWrapper size={size} className={className}>
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
        <circle cx="12" cy="10" r="3"></circle>
    </IconWrapper>
);
const PhoneIcon = ({ size = 20, className = '' }) => (
    <IconWrapper size={size} className={className}>
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-5.32-5.32 19.79 19.79 0 0 1-3.07-8.63A2 2 0 0 1 3.08 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
    </IconWrapper>
);
const MailIcon = ({ size = 20, className = '' }) => (
    <IconWrapper size={size} className={className}>
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
        <polyline points="22,6 12,13 2,6"></polyline>
    </IconWrapper>
);
const ClockIcon = ({ size = 20, className = '' }) => (
    <IconWrapper size={size} className={className}>
        <circle cx="12" cy="12" r="10"></circle>
        <polyline points="12 6 12 12 16 14"></polyline>
    </IconWrapper>
);
const GlobeIcon = ({ size = 20, className = '' }) => (
    <IconWrapper size={size} className={className}>
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="2" y1="12" x2="22" y2="12"></line>
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
    </IconWrapper>
);

// --- End of Inline SVG Icon Components ---

const Footer = () => {
    const currentYear = 2025;
    
    // --- CONTACT INFORMATION ---
    const PRIMARY_PHONE = '9710366466';
    const SECONDARY_PHONE = '9962310466';
    const EMAIL_ADDRESS = 'interiorsdred@gmail.com';
    const WEBSITE_URL = 'www.dredinteriors.com';
    const INSTAGRAM_HANDLE = 'dredinteriors_';
    const YOUTUBE_HANDLE = 'dredinteriorz';
    // Studio address (Neelankarai)
    const ADDRESS_SECONDARY = '#4/591 Vaithiyalingam salai, Neelankarai, Chennai - 115';

    // Nav links imported from the Navbar component structure
    const navLinks = [
        { to: "/", name: "Home" },
        { to: "/about", name: "About" },
        { to: "/services", name: "Services" },
        { to: "/projects", name: "Projects" },
        { to: "/materials", name: "Materials" },
        { to: "/contact", name: "Contact" },
    ];

    // UPDATED social links: Only Instagram, YouTube, and WhatsApp
    const socialLinks = [
        { icon: WhatsappChatIcon, href: `https://wa.me/91${PRIMARY_PHONE}`, name: 'WhatsApp' }, 
        { icon: InstagramIcon, href: `https://instagram.com/${INSTAGRAM_HANDLE}`, name: 'Instagram' },
        { icon: YoutubeIcon, href: `https://youtube.com/@${YOUTUBE_HANDLE}`, name: 'YouTube' },
    ];

    return (
        <footer className="bg-secondary text-white pt-16 pb-8 px-4">
            {/* Custom CSS for the glowing pulse animation (Unchanged) */}
            <style jsx="true">{`
                @keyframes pulse-glow {
                    0%, 100% { 
                        /* Initial state (Gray/White glow) */
                        text-shadow: 0 0 5px rgba(255, 255, 255, 0.4), 0 0 10px rgba(255, 255, 255, 0.2); 
                        opacity: 1;
                    }
                    50% { 
                        /* Peak glow (Red/Primary color glow) */
                        text-shadow: 0 0 10px rgba(239, 68, 68, 0.8), 0 0 20px rgba(239, 68, 68, 0.5); 
                        opacity: 0.9;
                    }
                }
                /* Applied to the entire 'p' tag containing the link */
                .siteora-glow-container {
                    animation: pulse-glow 2s infinite ease-in-out;
                }
            `}</style>

            <div className="max-w-7xl mx-auto">
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-12 border-b border-white/10 pb-12">
                    
                    {/* Column 1: About & Logo */}
                    <div className="col-span-1">
                        <Link 
                            to="/" 
                            className="flex items-center mb-4 text-2xl font-heading font-bold text-primary transition duration-200"
                        >
                            <img 
                                src={Logo} 
                                alt="D Red Interiors Logo" 
                                className="h-24 w-auto p-3 object-contain" 
                            />
                        </Link>
                        
                        <p className="text-gray-400 mb-6 text-sm leading-relaxed">
                            Creating beautiful, functional spaces that reflect your personality and lifestyle through innovative design solutions.
                        </p>
                        
                        {/* Social Links (Now only WhatsApp, Instagram, and YouTube) */}
                        <div className="flex space-x-3">
                            {socialLinks.map((link, index) => (
                                <a 
                                    key={index} 
                                    href={link.href} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    title={link.name}
                                    className="flex items-center justify-center w-10 h-10 rounded-full bg-white/10 text-white transition duration-300 hover:bg-primary hover:scale-105"
                                >
                                    <link.icon size={18} />
                                </a>
                            ))}
                        </div>
                    </div>

                    {/* Column 2: Quick Links (Unchanged) */}
                    <div>
                        <h3 className="font-heading text-xl font-semibold mb-6">Quick Links</h3>
                        <ul className="space-y-3">
                            {navLinks.map((link, index) => (
                                <li key={index}>
                                    <Link 
                                        to={link.to} 
                                        className="text-gray-400 text-sm py-1 pl-3 border-l-2 border-transparent transition duration-300 ease-in-out hover:border-primary hover:text-primary block"
                                    >
                                        {link.name}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Column 3: Contact Information (Updated Contact Details) */}
                    <div>
                        <h3 className="font-heading text-xl font-semibold mb-6">Contact Us</h3>
                        <ul className="space-y-4 text-gray-400 text-sm">
                            {/* Studio Address */}
                            <li className="flex items-start">
                                <MapPinIcon className="mr-3 flex-shrink-0 text-primary" size={20} />
                                <span className="text-white/80">
                                    {/* Splitting the new address for better readability */}
                                    {ADDRESS_SECONDARY.split(',').map((line, index) => (
                                        <React.Fragment key={index}>
                                            {line.trim()}
                                            {index < ADDRESS_SECONDARY.split(',').length - 1 && <br />}
                                        </React.Fragment>
                                    ))}
                                </span>
                            </li>
                            
                            {/* Phone Numbers */}
                            <li className="flex items-center">
                                <PhoneIcon className="mr-3 flex-shrink-0 text-primary" size={20} />
                                {/* Primary Phone */}
                                <a href={`tel:+91${PRIMARY_PHONE}`} className="hover:text-primary transition duration-200">
                                    +(91) {PRIMARY_PHONE.substring(0, 5)} {PRIMARY_PHONE.substring(5)}
                                </a>
                                <span className="mx-2">/</span>
                                {/* Secondary Phone */}
                                <a href={`tel:+91${SECONDARY_PHONE}`} className="hover:text-primary transition duration-200">
                                    {SECONDARY_PHONE.substring(0, 5)} {SECONDARY_PHONE.substring(5)}
                                </a>
                            </li>
                            
                            {/* Email */}
                            <li className="flex items-center">
                                <MailIcon className="mr-3 flex-shrink-0 text-primary" size={20} />
                                <a href={`mailto:${EMAIL_ADDRESS}`} className="hover:text-primary transition duration-200">{EMAIL_ADDRESS}</a>
                            </li>

                            {/* Website */}
                            <li className="flex items-center">
                                <GlobeIcon className="mr-3 flex-shrink-0 text-primary" size={20} />
                                <a href={`http://${WEBSITE_URL}`} target="_blank" rel="noopener noreferrer" className="hover:text-primary transition duration-200">{WEBSITE_URL}</a>
                            </li>

                            {/* Opening Hours (Kept generic as none were provided) */}
                            <li className="flex items-center">
                                <ClockIcon className="mr-3 flex-shrink-0 text-primary" size={20} />
                                <span>Monday-Sunday: 9AM - 7PM</span>
                            </li>
                        </ul>
                    </div>
                </div>
                
                {/* Copyright Section (Unchanged) */}
                <div className="text-center pt-8 text-gray-500 text-sm space-y-2">
                    <p>&copy; {currentYear} D Red Interiors. All rights reserved.</p>
                    
                    <p className="text-sm text-gray-400 siteora-glow-container">
                        Designed and developed by {' '}
                        <a 
                            href="https://siteora.online" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-light font-bold hover:text-purple-300 transition duration-300 ease-in-out inline-block"
                        >
                            siteora
                        </a>
                    </p>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
